import {
  Button,
  Link,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Center,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { BreadcrumbElement } from "../../components/chakra/Breadcrumbs";
import ChakraScreenContainer from "../../components/chakra/ChakraScreenContainer";
import NotFound from "../NotFound";
import RecordForm from "../../components/chakra/form/RecordForm";
import ReadOnlyRecord from "../../components/chakra/record/ReadOnlyRecord";
import { Card, CardContent, CardHeading } from "../../components/chakra/card";
import Icon from "../../components/UI/Icon";
import * as CustomFieldTypeID from "../../shared/v2/constants/CustomFieldTypeID";
import { CustomFieldRecord } from "../../shared/v2/definitions/customFields";
import { List } from "../../shared/v2/definitions/lists";
import { useApiQuery } from "../../utilities/apibelRequest";
import Spinner from "../../components/chakra/common/Spinner";

const TEST_DATA: {
  customFields: {
    customField: CustomFieldRecord;
    fieldProps: {
      label: string;
      unitLabel?: string;
      isRequired?: boolean;
    };
  }[];
  name: string;
  description: string | null;
} = {
  name: "Some Asset",
  description: "<p><strong>Wow!</strong></p><p>Some cool description</p>",
  customFields: [
    {
      customField: {
        customFieldID: "g392ojg-g30gjpg3",
        customFieldTypeID: CustomFieldTypeID.Text,
        value: "Lorum Ipsum",
      },
      fieldProps: {
        label: "Some Text Field",
      },
    },
    {
      customField: {
        customFieldID: "23qop-tiojas",
        customFieldTypeID: CustomFieldTypeID.Text,
        value: null,
      },
      fieldProps: {
        label: "A Blank Field",
        unitLabel: "units",
      },
    },
    {
      customField: {
        customFieldID: "opwa9oh-g42iog00",
        customFieldTypeID: CustomFieldTypeID.Number,
        value: 200,
      },
      fieldProps: {
        label: "How Many?",
      },
    },
    {
      customField: {
        customFieldID: "h42pkh24-249t42g",
        customFieldTypeID: CustomFieldTypeID.LongText,
        value: "<p>Poggers</p><p>Second paragraph.</p>",
      },
      fieldProps: {
        label: "Bigly Text",
      },
    },
    {
      customField: {
        customFieldID: "opwa9oh-g42iog00",
        customFieldTypeID: CustomFieldTypeID.Number,
        value: 64,
      },
      fieldProps: {
        label: "Weight",
        unitLabel: "kg",
      },
    },
    {
      customField: {
        customFieldID: "opwa9oh-g42iog00",
        customFieldTypeID: CustomFieldTypeID.SingleSelectList,
        value: ["y"],
      },
      fieldProps: {
        label: "Is Employed?",
      },
    },
  ],
};

const TEST_LIST_DATA: Record<string, List> = {
  ls: {
    listID: "ls",
    name: "some list",
    listTypeID: "1f484656-ed84-4f64-b8ad-4af16daee7ab",
    options: [
      {
        id: "y",
        value: "Yes",
      },
      {
        id: "n",
        value: "No",
      },
    ],
  },
};

const BREADCRUMBS: BreadcrumbElement[] = [
  {
    label: "Home",
    link: "/",
  },
  {
    label: "Registers",
    link: "/registernew",
  },
];
const RegisterRecordScreen = () => {
  const params = useParams();
  const location = useLocation();

  const [isEditingRecord, setIsEditingRecord] = useState(false);

  const passedObjectType: { name: string; ID: string } | undefined =
    location.state?.objectType;
  const passedRecordData: { name: string, readableID: string } | undefined = location.state?.recordData;


  const { recordID } = params;

  const record = useApiQuery(
    "register/recordByID",
    { ID: recordID || "" },
    { enabled: !!recordID },
  );

  if (recordID === undefined) {
    return <NotFound />;
  }
  
  const objectType = record.isSuccess ? { name: record.data.objectTypeNamePlural, ID: record.data.objectTypeID } : passedObjectType;
  const recordTitle: string | undefined = record.data?.name || passedRecordData?.name;
  const recordReadableID: string | undefined = record.data?.readableID || passedRecordData?.readableID;

  return (
    <chakra-scope>
      <ChakraScreenContainer
        contentSize="container.lg"
        breadcrumbs={
          objectType !== undefined
            ? [
                ...BREADCRUMBS,
                {
                  label: objectType.name,
                  link: `/registernew/dashboard/${objectType.ID}`,
                },
              ]
            : BREADCRUMBS
        }
        pageTitle={recordTitle || "Register Record"}
        pageSubtitle={recordReadableID ? `Register - ${recordReadableID}` : recordTitle ? undefined : "Register Record"}>
        <Box mb="8">
          <Alert status="warning">
            <AlertIcon />
            <Box>
              <AlertTitle>This page is not yet fully implemented.</AlertTitle>
              <AlertDescription display="block">
                <Link
                  color="blue"
                  as={RouterLink}
                  to={`/register/${recordID}?display=registers`}>
                  Click here
                </Link>{" "}
                to go to the legacy register record page.
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
        {record.isSuccess ? (
          <>
            <Card>
              <CardHeading title="Details">
                {isEditingRecord ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => setIsEditingRecord(false)}>
                    Cancel
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Icon name="Edit2" />}
                    onClick={() => setIsEditingRecord(true)}>
                    Edit
                  </Button>
                )}
              </CardHeading>
              <CardContent>
                {isEditingRecord ? (
                  <RecordForm record={record.data} />
                ) : (
                  <ReadOnlyRecord record={record.data} />
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <Center>
            <Spinner />
          </Center>
        )}
      </ChakraScreenContainer>
    </chakra-scope>
  );
};

export default RegisterRecordScreen;
